"use client"
import Image, { ImageProps } from "next/image"
import { useState } from "react"
import { PLACEHOLDER_IMAGE } from "@/lib/article-image"
import { isLosslessGraphicUrl, optimizeRemoteImageUrl } from "@/utils/helpers/utils"

export interface OptimizedImageProps extends ImageProps {
   src: string
   fallbackSrc?: string
   loadingComponent?: React.ReactNode
   priority?: boolean
   cloudfront?: boolean
   preload?: boolean // Preload prop
}

const Img: React.FC<OptimizedImageProps> = ({
   src,
   alt,
   fallbackSrc = PLACEHOLDER_IMAGE,
   priority = false,
   cloudfront = true,
   // eslint-disable-next-line unused-imports/no-unused-vars
   preload = false, // Preload images if needed
   quality = 70,
   unoptimized,
   ...props
}) => {
   const [imageSrc] = useState(() => {
      if (src === PLACEHOLDER_IMAGE) return src

      // Already-absolute URLs (full CloudFront or Unsplash) pass through the
      // remote-URL optimizer without being prefixed again.
      if (/^https?:\/\//i.test(src)) return optimizeRemoteImageUrl(src)

      // Only prepend the CDN base for CloudFront-hosted assets when we actually
      // have a base. `NEXT_PUBLIC_CLOUDFRONT_URL` must be set for the client
      // bundle; falling back to `src` keeps a missing env var from emitting an
      // `undefined/...` src that the Next.js image optimizer rejects.
      const cdnBase = process.env.NEXT_PUBLIC_CLOUDFRONT_URL ?? process.env.CLOUDFRONT_URL ?? ""
      return cloudfront && cdnBase ? `${cdnBase}${src}` : src
   })

   const [isLoading] = useState(false)
   const [hasError, setHasError] = useState(false)

   // PNG/SVG/GIF graphics are served untouched (see isLosslessGraphicUrl);
   // everything else goes through the Next.js optimizer for resizing and
   // AVIF/WebP re-encoding. An explicit `unoptimized` prop always overrides this.
   const shouldSkipOptimization = unoptimized ?? isLosslessGraphicUrl(imageSrc)

   return (
      <Image
         unoptimized={shouldSkipOptimization}
         quality={quality}
         src={hasError ? fallbackSrc : imageSrc}
         alt={alt}
         decoding="async"
         loading={priority ? "eager" : "lazy"}
         onError={() => setHasError(true)}
         // Debug aid: "error" = a real src failed to load, "empty" = the slot had no image at all.
         data-image-state={hasError ? "error" : imageSrc === PLACEHOLDER_IMAGE ? "empty" : undefined}
         style={{
            opacity: isLoading ? 0 : 1,
            transition: "opacity 0.5s ease-in-out",
            ...props.style,
         }}
         {...props}
      />
   )
}

export default Img
